/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {AppConfigService} from './app-config.service';
import {PlanModelService} from './plan-model.service';
import {AttachmentModel} from 'src/app/common/models/attachment-model';
import {AlertModalService} from '../component/modal';
import {PLAN_LODGEMENT_STAGE, PLAN_LODGEMENT_STAGE_ACRONYM} from '../constants';
import {catchError, map, Observable, of, Subject, throwError} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ArcGisAuthService} from '../../design/services/arc-gis-auth.service';
import {GeoProcessingService} from '../../design/services/geo-processing-service';

export interface Opts {
    spatialReference: string;
    referenceScale: string;
    paperSize: string;
    printHiddenFeatures: boolean;
    printEnlargementExtents: boolean;
    watermarkText: string;
}

export interface JobInfo {
    jobStatus: string;
    results: any;
}

@Injectable({
    providedIn: 'root'
})
export class PlanPdfService {
    title = 'Plan PDF Service';

    constructor(
        private alertModalService: AlertModalService,
        private appConfigService: AppConfigService,
        private arcGisAuthService: ArcGisAuthService,
        private geoProcessingService: GeoProcessingService,
        private http: HttpClient,
        private planModelService: PlanModelService
    ) {}

    generateAndDisplayPdf(opts: Opts, printPdf: boolean) {
        console.debug(this.title + ': generateAndDisplayPdf');

        const planLodgement = this.planModelService.getModel();

        const spatialReference: any = opts.spatialReference;
        const referenceScale = opts.referenceScale;
        const paperSize = opts.paperSize;
        const printHiddenFeatures = opts.printHiddenFeatures;
        const printEnlargementExtents = opts.printEnlargementExtents;
        const watermarkText = opts.watermarkText && this.showWatermark(planLodgement) ? opts.watermarkText : '';

        const guid = planLodgement.guid;
        const resolution = 300;
        const errorMessage = 'An error occurred while printing the PDF';

        // Only save the plan before generating the PDF if the plan has not been lodged yet
        // Saving a lodged plan is invalid and will result in an error
        const saveObservable =
            this.planModelService.getModel().planLodgementStage === PLAN_LODGEMENT_STAGE.LODGED ? of(null) : this.planModelService.save();

        return saveObservable.pipe(
            switchMap(() => {
                const geoProcessingParameters = {
                    mapScale: referenceScale,
                    spatialReference: spatialReference.wkid,
                    paperSize: paperSize,
                    resolution: resolution,
                    guid: guid,
                    showAll: printHiddenFeatures,
                    showEnlargements: printEnlargementExtents
                };

                return this.geoProcessingService.callGeoProcessingService(
                    this.appConfigService.getDesignProperties().DESIGN_WIDGETS_CONFIGURATION.EXPORTPLAN.GP_URL +
                        this.arcGisAuthService.getTokenParam(),
                    geoProcessingParameters,
                    false
                );
            }),
            switchMap((jobInfo: any) => {
                const jobStatus = jobInfo.jobStatus;
                console.debug(`${this.title} - generateAndDisplayPdf - jobStatus:`, jobStatus);

                const subject = new Subject<void>();

                if (jobStatus === 'esriJobSucceeded') {
                    const exportedPlanImages = jobInfo.results.exportedPlanImages.featureResult.value;
                    const title = this.buildPdfTitle(planLodgement);

                    let url = this.appConfigService.settings.apiGatewayUrl;
                    let request: any;

                    if (printPdf) {
                        url = url + '/pdf/printPDF';
                        request = {
                            planLodgementId: planLodgement.id,
                            paperSize: paperSize,
                            exportedPlanImages: exportedPlanImages,
                            watermarkText: watermarkText,
                            printScale: referenceScale
                        };
                    } else {
                        url = url + '/pdf/generatePlanPDF';
                        request = {
                            planLodgementId: planLodgement.id,
                            paperSize: paperSize,
                            exportedPlanImages: exportedPlanImages,
                            watermarkText: watermarkText,
                            title: title
                        };
                    }

                    this.http
                        .post(url, request, {
                            responseType: 'arraybuffer'
                        })
                        .subscribe({
                            next: (res: any) => {
                                if (!printPdf) {
                                    this.displayPdf(title, res);
                                }
                                subject.next();
                                subject.complete();
                            },
                            error: error => {
                                console.error(`${this.title} - generateAndDisplayPdf - error:`, error);
                                this.alertModalService.createAlertMessage(errorMessage);
                                subject.error(errorMessage);
                            }
                        });
                } else if (jobStatus === 'esriJobFailed') {
                    console.error(`${this.title} - generateAndDisplayPdf - failed`);
                    this.alertModalService.createAlertMessage(errorMessage);
                    subject.error(errorMessage);
                }

                return subject;
            })
        );
    }

    validatePlanPdf(guid: string, attachment: AttachmentModel): Observable<any> {
        const url = this.appConfigService.settings.apiGatewayUrl + '/pdf/validatePlanPdf';

        const request = {
            planLodgementId: this.planModelService.getModel().id,
            guid: guid
        };

        return this.http.post<any>(url, request).pipe(
            map(data => {
                const validationResponse = data;
                console.debug(validationResponse);
                const errorLabelFields = [];
                if (validationResponse.error) {
                    if (validationResponse.errors !== undefined && validationResponse.errors.length > 0) {
                        validationResponse.errors.forEach((error: string) => {
                            errorLabelFields.push(error);
                        });
                        this.alertModalService.createAlertMessage(errorLabelFields, 'Ok', 'Invalid Attachment', 'error');
                    }
                }
                return data;
            }),
            catchError(error => {
                return throwError(() => {
                    new Error(error);
                });
            })
        );
    }

    isLandgateSigned(base64EncodedPdf: string) {
        const url = this.appConfigService.settings.apiGatewayUrl + '/pdf/isLandgateSigned';
        return this.http.get<[]>(url);
    }
    /**
     * Displays the PDF in a popup.
     * @param title the popup window title.
     * @param data the Base64 encoded PDF data to be displayed.
     */
    displayPdf(title: string, data: ArrayBuffer) {
        const file = new Blob([data], {type: 'application/pdf'});
        const fileURL = URL.createObjectURL(file);
        const params = ['height=' + screen.height, 'width=' + screen.width, 'fullscreen=yes'].join(',');

        const popup = window.open(fileURL, title, params);

        if (popup === null) {
            this.alertModalService.createAlertMessage(
                'A window could not be opened due to a pop-up blocker. Please enable pop-ups in the browser and try again.',
                'OK',
                'Window blocked',
                'error'
            );
            return;
        }

        popup.moveTo(0, 0);
        popup.onload = () => {
            const link = popup.document.createElement('a');
            link.href = fileURL;
            link.download = title;
            popup.document.body.appendChild(link);
            link.click();
        };
    }

    showWatermark(planLodgement) {
        return (
            !planLodgement.planLodgementStage ||
            [PLAN_LODGEMENT_STAGE.IN_DESIGN, PLAN_LODGEMENT_STAGE.READY_FOR_QA].includes(planLodgement.planLodgementStage)
        );
    }

    checkIsLandgateSigned(base64EncodedPdf: string): Observable<boolean> {
        const url = this.appConfigService.settings.apiGatewayUrl + '/pdf/isLandgateSigned';
        return this.http.post<boolean>(url, base64EncodedPdf);
    }

    buildPdfTitle(planLodgement: any) {
        const smpId = planLodgement.planType.smpId;
        const planNumber = planLodgement.planNumber;
        const versionNumber = planLodgement.versionNumber;
        const planLodgementStage = PLAN_LODGEMENT_STAGE_ACRONYM[planLodgement.planLodgementStage];
        const formattedDate = this.getFormattedDate();
        return `${smpId}${planNumber}_v${versionNumber}_${planLodgementStage}_${formattedDate}`;
    }

    private getFormattedDate() {
        const d = new Date();
        return (
            `${this.pad(d.getDate())}${this.pad(d.getMonth() + 1)}${this.pad(d.getFullYear())}` +
            `_${this.pad(d.getHours())}${this.pad(d.getMinutes())}`
        );
    }

    private pad(v: number) {
        return `0${v}`.slice(-2);
    }
}
